import React, { useState } from "react";
import { Flex, Button, ModalV2, FileUpload, FileObjectType, toast, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { DataStudioJobSubstateEnum, DataStudioJobTypeEnum } from "~/graphql/supergraphEnums";
import { useCrmImportJobConfig } from "../../../hooks/useCrmImportQueries";
import useCrmImportUpload from "../../../hooks/useCrmImportUpload";
import { useIdQueryParam } from "../../../hooks/useIdQueryParam";
import { VALIDATION_ENUM_TO_JOB_CONFIG_TOOL_TYPE_MAP } from "../constants";
import {
  getDataTypeByToolType,
  sortToolsByPriority,
  getValidationKeyForDataType,
} from "../utils";
import * as styles from "./styles_vanilla.css";

interface ReplaceFileModalProps {
  showModal: boolean;
  onClose: () => void;
  fileName: string;
  toolType: string;
  onFileReplaced?: () => void;
}

const ReplaceFileModal: React.FC<ReplaceFileModalProps> = ({
  showModal,
  onClose,
  fileName,
  toolType,
  onFileReplaced,
}) => {
  const { idFromUrl } = useIdQueryParam();
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileObjectType[]>([]);

  const { uploadFilesToS3, createJob } = useCrmImportUpload();

  const { data: jobConfigData } = useCrmImportJobConfig(idFromUrl || "", {
    skip: !idFromUrl,
  });

  const handleProceed = () => {
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleFilesSelected = (files: FileObjectType[]) => {
    const acceptedFiles = files.filter((file) => file.status === "accepted");
    setSelectedFiles(acceptedFiles);
  };

  const handleReplaceFile = async () => {
    if (!selectedFiles.length || !jobConfigData || !idFromUrl) {
      toast.error("Please select a file to upload");
      return;
    }

    setIsUploading(true);

    try {
      // Upload the new file
      const _uploadResults = await uploadFilesToS3({
        files: selectedFiles,
        environment: jobConfigData.environment,
        jobType: "CRM_IMPORT",
        arenaDomain: jobConfigData.arenaDomain,
        arenaUserEmail: jobConfigData.arenaUserEmail,
      });

      const jobConfigToolType =
        VALIDATION_ENUM_TO_JOB_CONFIG_TOOL_TYPE_MAP[toolType] || toolType;

      const toolToUpdate = jobConfigData.parsedJobConfig?.tools?.find((tool) => {
        return tool.tool_type === jobConfigToolType;
      });

      if (!toolToUpdate) {
        throw new Error("Could not find the tool configuration for this file");
      }

      // Update the job configuration with the new file and sort by predefined order
      const updatedTools = jobConfigData.parsedJobConfig?.tools?.map((tool) => {
        if (tool.tool_id === toolToUpdate.tool_id) {
          const updatedOptions = tool.options?.map((option) => {
            if (option.key.endsWith("_file")) {
              return {
                ...option,
                value: `${jobConfigData.arenaDomain}/${selectedFiles[0].file.name}`,
              };
            }
            return option;
          });

          return {
            toolId: tool.tool_id,
            toolType: tool.tool_type,
            options: updatedOptions || [],
          };
        }

        return {
          toolId: tool.tool_id,
          toolType: tool.tool_type,
          options: tool.options || [],
        };
      });

      // Sort tools by predefined priority order
      const sortedTools = sortToolsByPriority(updatedTools || []);

      // Determine the data type for the replaced file to include in toolsToValidate
      const dataTypeForValidation = getDataTypeByToolType(toolToUpdate.tool_type);

      // Map the data type to the correct toolsToValidate value using the mapping
      const toolToValidate = dataTypeForValidation
        ? getValidationKeyForDataType(dataTypeForValidation)
        : undefined;

      // Update the job with the new file configuration
      await createJob({
        jobType: DataStudioJobTypeEnum.CrmImport,
        arenaDomain: jobConfigData.arenaDomain,
        arenaUserEmail: jobConfigData.arenaUserEmail,
        env: jobConfigData.environment,
        jobId: idFromUrl,
        jobConfig: {
          tools: sortedTools,
        },
        substate: DataStudioJobSubstateEnum.DataImport,
        ...(jobConfigData?.createdAt && { createdAt: jobConfigData.createdAt }),
        ...(toolToValidate && { toolsToValidate: [toolToValidate] }),
      });

      toast.success(`File ${fileName} has been replaced successfully`);
      setShowConfirmation(true); // Reset for next time
      onClose();

      onFileReplaced?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(`Failed to replace file: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setShowConfirmation(true); // Reset to confirmation step when modal closes
    onClose();
  };

  return (
    <ModalV2
      show={showModal}
      onHide={handleClose}
      title={showConfirmation ? "Replace File and Trigger Validation" : `Replace ${fileName}`}
      size="medium"
    >
      <ModalV2.Body>
        {showConfirmation ? (
          <Flex flexDirection="column" gap={Sizes.LG}>
            <Text variant="regular-md">This action will perform two steps:</Text>
            <Flex flexDirection="column" gap={Sizes.MD} className={styles.stepsList}>
              <Flex flexDirection="row" gap={Sizes.SM} alignItems="flex-start">
                <Text variant="strong-md" className={styles.stepNumber}>
                  1.
                </Text>
                <Flex flexDirection="column" gap={Sizes.XS}>
                  <Text variant="strong-md">Update the file:</Text>
                  <Text variant="secondary-sm">
                    Replace the current file with your new upload
                  </Text>
                </Flex>
              </Flex>
              <Flex flexDirection="row" gap={Sizes.SM} alignItems="flex-start">
                <Text variant="strong-md" className={styles.stepNumber}>
                  2.
                </Text>
                <Flex flexDirection="column" gap={Sizes.XS}>
                  <Text variant="strong-md">Trigger record validation:</Text>
                  <Text variant="secondary-sm">
                    Automatically start validation on the new file
                  </Text>
                </Flex>
              </Flex>
            </Flex>
            <Text variant="secondary-sm" className={styles.redirectionNote}>
              You will be redirected to the validation loader to monitor progress.
            </Text>
          </Flex>
        ) : (
          <Flex flexDirection="column" gap={Sizes.MD}>
            <Text variant="regular-md">
              Select a new file to replace{" "}
              <Text variant="strong-md" asSpan>
                {fileName}
              </Text>
              :
            </Text>
            <FileUpload
              canAllowMultiple={false}
              onFileSelected={handleFilesSelected}
              acceptedFileTypes={[".csv", ".tsv", "text/csv", "text/tab-separated-values"]}
              isFullWidth={true}
            />
          </Flex>
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" flexWrap="nowrap" justifyContent="flex-end" gap={Sizes.SM}>
          <Button
            variant="neutral"
            type="button"
            onClick={showConfirmation ? handleCancel : handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          {showConfirmation ? (
            <Button variant="primary" onClick={handleProceed}>
              Proceed
            </Button>
          ) : (
            <Button
              variant="primary"
              isLoading={isUploading}
              onClick={handleReplaceFile}
              disabled={!selectedFiles.length || isUploading}
            >
              {isUploading ? "Replacing..." : "Replace File"}
            </Button>
          )}
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default ReplaceFileModal;
